/**
 * Sanitizes HTML content for iframe injection to prevent navigation
 * from affecting the parent window.
 *
 * This function:
 * 1. Ensures all anchor tags with hash links stay within the iframe
 * 2. Prevents links from targeting parent window
 * 3. Adds event handlers to prevent hash navigation from bubbling up
 *
 * @param html - The raw HTML string from the API
 * @returns Sanitized HTML string safe for iframe srcDoc
 */
export function sanitizePreviewHtml(html: string | null): string {
  if (!html) {
    return "";
  }

  try {
    // Create a temporary DOM parser to manipulate the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Check for parsing errors
    const parserError = doc.querySelector("parsererror");
    if (parserError) {
      console.warn(
        "HTML parsing error, using original HTML with script injection",
      );
      // Fallback: inject script directly into HTML string
      return injectNavigationScript(html);
    }

    // Process all anchor tags
    const anchors = doc.querySelectorAll("a");
    anchors.forEach((anchor) => {
      const href = anchor.getAttribute("href");

      if (href) {
        // Remove any target attributes that might navigate parent
        if (
          anchor.getAttribute("target") === "_parent" ||
          anchor.getAttribute("target") === "_top"
        ) {
          anchor.removeAttribute("target");
        }

        // For external links, ensure they open in new tab
        if (href.startsWith("http://") || href.startsWith("https://")) {
          anchor.setAttribute("target", "_blank");
          anchor.setAttribute("rel", "noopener noreferrer");
        }
      }
    });

    // Add a script to handle hash navigation and prevent parent window navigation
    const script = doc.createElement("script");
    script.textContent = getNavigationScript();

    // Insert script before closing body tag, or append to head if no body
    const body = doc.body;
    if (body) {
      body.appendChild(script);
    } else {
      // If no body, create one or append to head
      const head = doc.head;
      if (head) {
        head.appendChild(script);
      } else {
        // Last resort: create body and append
        const newBody = doc.createElement("body");
        newBody.appendChild(script);
        doc.documentElement.appendChild(newBody);
      }
    }

    // Serialize back to HTML string
    return doc.documentElement.outerHTML;
  } catch (error) {
    console.error("Error sanitizing HTML, using fallback:", error);
    // Fallback: inject script directly into HTML string
    return injectNavigationScript(html);
  }
}

/**
 * Returns the navigation prevention script as a string
 */
function getNavigationScript(): string {
  return `
    (function() {
      'use strict';
      
      // Prevent hash changes from affecting parent window
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;
      
      history.pushState = function() {
        originalPushState.apply(history, arguments);
        // Hash navigation is contained within iframe
      };
      
      history.replaceState = function() {
        originalReplaceState.apply(history, arguments);
        // Hash navigation is contained within iframe
      };

      // Intercept all anchor clicks to prevent parent navigation
      document.addEventListener('click', function(e) {
        const anchor = e.target.closest('a');
        if (!anchor) return;
        
        const href = anchor.getAttribute('href');
        if (!href) return;
        
        // For hash links, handle smooth scroll within iframe
        if (href.startsWith('#')) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          const targetId = href.substring(1);
          if (!targetId) {
            // Just # means scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });
            history.replaceState(null, '', '#');
            return false;
          }
          
          const targetElement = document.getElementById(targetId) || 
                               document.querySelector('[name="' + targetId + '"]') ||
                               document.querySelector('[id="' + targetId + '"]');
          
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Update URL hash without triggering parent navigation
            history.replaceState(null, '', href);
          }
          return false;
        }
        
        // For external links, they should already have target="_blank"
        // For relative/internal links, prevent any navigation that might affect parent
        if (!href.startsWith('http://') && !href.startsWith('https://') && !href.startsWith('mailto:') && !href.startsWith('tel:')) {
          // This is a relative link - prevent it from affecting parent
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      }, true); // Use capture phase to catch early

      // Prevent hashchange events from bubbling
      window.addEventListener('hashchange', function(e) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, true);
      
      // Prevent popstate events from bubbling
      window.addEventListener('popstate', function(e) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, true);
    })();
  `;
}

/**
 * Fallback function to inject script directly into HTML string
 */
function injectNavigationScript(html: string): string {
  const script = getNavigationScript();
  const scriptTag = `<script>${script}<\/script>`;

  // Try to inject before closing body tag
  const bodyCloseIndex = html.lastIndexOf("</body>");
  if (bodyCloseIndex !== -1) {
    return (
      html.slice(0, bodyCloseIndex) + scriptTag + html.slice(bodyCloseIndex)
    );
  }

  // Try to inject before closing html tag
  const htmlCloseIndex = html.lastIndexOf("</html>");
  if (htmlCloseIndex !== -1) {
    return (
      html.slice(0, htmlCloseIndex) + scriptTag + html.slice(htmlCloseIndex)
    );
  }

  // Last resort: append to end
  return html + scriptTag;
}
